import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { isAuthorized } from "../../../utils/authority";
import {
  Card,
  Button,
  Select,
  Spinner,
  MultiSelect,
  Badge,
} from "../../../shared";
import Tabs from "../../../shared/Tabs";
import { useGetCustomersQuery } from "../../../redux/services/customers/customersApi";
import request from "../../../utils/request";
import { toast } from "react-toastify";

type FeatureMeta = {
  lifecycle: "dev" | "beta" | "ga";
  defaultEnabled?: boolean;
  description?: string;
};

type FeatureFlagsAdminResponse = {
  status: "success" | "error";
  message?: string;
  parentCompany: any;
  planTier: "tier1" | "tier2" | "tier3" | "tier4";
  releaseChannel: "stable" | "latest";
  betaAllowlist: string[];
  overrides: Record<string, boolean>;
  resolvedFlags: Record<string, boolean>;
  adminFlags: Record<string, boolean>;
  sources: Record<string, string>;
  registry: Record<string, FeatureMeta>;
};

type TierDefaultsResponse = {
  status: "success" | "error";
  message?: string;
  tiers: {
    tier1: Record<string, boolean>;
    tier2: Record<string, boolean>;
    tier3: Record<string, boolean>;
    tier4: Record<string, boolean>;
  };
  registry: Record<string, FeatureMeta>;
};

const toOptions = (arr: Array<{ label: string; value: string }>) => arr;

/**
 * Feature key helpers
 *
 * We treat `module.enabled` (ex: `jobs.enabled`) as a "parent" flag.
 * Any other key starting with `module.` is considered a "sub-feature" of that module,
 * as long as the parent exists in the registry.
 */
const getModuleFromFeatureKey = (featureKey: string) =>
  String(featureKey || "").split(".")[0] || "";

const getModuleParentKey = (featureKey: string) => {
  const module = getModuleFromFeatureKey(featureKey);
  return module ? `${module}.enabled` : "";
};

const isModuleParentKey = (featureKey: string) =>
  Boolean(featureKey) &&
  featureKey.endsWith(".enabled") &&
  String(featureKey).split(".").length === 2;

const FeatureFlagsPage: React.FC = () => {
  // `Tabs` is implemented in JS and currently has no TS props typing, so TS infers `{}`.
  // Cast to `any` locally to avoid blocking valid props usage in this page.
  const TabsAny = Tabs as unknown as React.FC<any>;
  const { user } = useAuth();

  const canAccess = isAuthorized("superAdmin");
  const [selectedParentCompanyId, setSelectedParentCompanyId] =
    useState<string>("");
  const [loadingFlags, setLoadingFlags] = useState(false);
  const [flagsData, setFlagsData] = useState<FeatureFlagsAdminResponse | null>(
    null
  );

  const [draftPlanTier, setDraftPlanTier] = useState<
    "tier1" | "tier2" | "tier3" | "tier4"
  >("tier1");
  const [draftReleaseChannel, setDraftReleaseChannel] = useState<
    "stable" | "latest"
  >("stable");
  const [draftBetaAllowlist, setDraftBetaAllowlist] = useState<string[]>([]);
  const [draftOverrides, setDraftOverrides] = useState<Record<string, boolean>>(
    {}
  );

  const [tierData, setTierData] = useState<TierDefaultsResponse | null>(null);
  const [selectedTier, setSelectedTier] = useState<
    "tier1" | "tier2" | "tier3" | "tier4"
  >("tier1");
  const [draftTierToggles, setDraftTierToggles] = useState<
    Record<string, boolean>
  >({});

  const { data: customersResp, isLoading: customersLoading } =
    useGetCustomersQuery(
      { page: 1, limit: 200, legacy: false },
      { skip: !canAccess }
    );

  const customerOptions = useMemo(() => {
    const respAny = customersResp as any;
    // API shape (current): { data: Customer[] }
    // Legacy shapes (fallbacks): { customer: Customer[] } or { customers: Customer[] }
    const list =
      (Array.isArray(respAny?.data) && respAny.data) ||
      respAny?.customer ||
      respAny?.customers ||
      [];
    return (list || [])
      .map((c: any) => ({
        label: `${c?.name || "Unknown"}${c?.slug ? ` (${c.slug})` : ""}`,
        value: c?._id,
      }))
      .filter((o: any) => Boolean(o.value));
  }, [customersResp]);

  const betaFeatureOptions = useMemo(() => {
    const registry = flagsData?.registry || {};
    return Object.entries(registry)
      .filter(([_k, meta]) => meta?.lifecycle === "beta")
      .map(([k, meta]) => ({
        label: meta?.description ? `${k} — ${meta.description}` : k,
        value: k,
      }));
  }, [flagsData]);

  const nonDevRegistryEntries = useMemo(() => {
    const registry = tierData?.registry || flagsData?.registry || {};
    return Object.entries(registry).filter(
      ([_k, meta]) => meta?.lifecycle !== "dev"
    );
  }, [tierData, flagsData]);

  /**
   * Tier Defaults grouping:
   * - Show parents (`module.enabled`) as top-level rows
   * - When a parent is enabled, show a dropdown (collapsible) of its sub-features
   * - Standalone flags (no parent in registry) render as a normal row
   */
  const tierDefaultsRows = useMemo(() => {
    const entries = nonDevRegistryEntries || [];
    const registry = Object.fromEntries(entries) as Record<string, FeatureMeta>;

    type Group = {
      kind: "group";
      parentKey: string;
      parentMeta: FeatureMeta;
      children: Array<[string, FeatureMeta]>;
    };

    type Standalone = {
      kind: "standalone";
      featureKey: string;
      meta: FeatureMeta;
    };

    const groupsByModule = new Map<string, Group>();
    const standalone: Standalone[] = [];

    // Pass 1: create groups for any module parent that exists in registry
    for (const [featureKey, meta] of entries) {
      if (!isModuleParentKey(featureKey)) continue;
      const module = getModuleFromFeatureKey(featureKey);
      if (!module) continue;
      groupsByModule.set(module, {
        kind: "group",
        parentKey: featureKey,
        parentMeta: meta,
        children: [],
      });
    }

    // Pass 2: assign children to groups when the parent exists; otherwise keep standalone
    for (const [featureKey, meta] of entries) {
      const module = getModuleFromFeatureKey(featureKey);
      const parentKey = getModuleParentKey(featureKey);
      const parentExists = Boolean(registry?.[parentKey]);

      // Parent rows are rendered via groups, don't duplicate as standalone
      if (isModuleParentKey(featureKey) && parentExists) continue;

      if (
        module &&
        parentExists &&
        featureKey !== parentKey &&
        groupsByModule.has(module)
      ) {
        const group = groupsByModule.get(module);
        group?.children.push([featureKey, meta]);
        continue;
      }

      standalone.push({ kind: "standalone", featureKey, meta });
    }

    const groups = Array.from(groupsByModule.values())
      .map((g) => ({
        ...g,
        children: (g.children || []).sort((a, b) => a[0].localeCompare(b[0])),
      }))
      .sort((a, b) => a.parentKey.localeCompare(b.parentKey));

    const standaloneSorted = (standalone || []).sort((a, b) =>
      a.featureKey.localeCompare(b.featureKey)
    );

    return [...groups, ...standaloneSorted] as Array<Group | Standalone>;
  }, [nonDevRegistryEntries]);

  useEffect(() => {
    if (!flagsData) return;
    setDraftPlanTier(flagsData.planTier || "tier1");
    setDraftReleaseChannel(flagsData.releaseChannel || "stable");
    setDraftBetaAllowlist(
      Array.isArray(flagsData.betaAllowlist) ? flagsData.betaAllowlist : []
    );
    setDraftOverrides(flagsData.overrides || {});
  }, [flagsData]);

  useEffect(() => {
    if (!tierData) return;
    const currentTier = tierData.tiers?.[selectedTier] || {};
    setDraftTierToggles({ ...(currentTier || {}) });
  }, [tierData, selectedTier]);

  async function loadTierDefaults() {
    try {
      const res = await request(`/admin/feature-flags/tiers`, {
        method: "get",
      });
      setTierData(res.data as TierDefaultsResponse);
    } catch (err: any) {
      console.error(err);
      toast(err?.response?.data?.message || "Failed to load tier defaults", {
        type: "error",
        toastId: "tier-defaults-load-failed",
      });
    }
  }

  async function saveTierDefaults() {
    try {
      const res = await request(`/admin/feature-flags/tiers`, {
        method: "patch",
        data: {
          tier: selectedTier,
          featureToggles: draftTierToggles,
        },
      });
      setTierData(res.data as TierDefaultsResponse);
      toast("Tier defaults updated", {
        type: "success",
        toastId: "tier-defaults-saved",
      });
    } catch (err: any) {
      console.error(err);
      toast(err?.response?.data?.message || "Failed to save tier defaults", {
        type: "error",
        toastId: "tier-defaults-save-failed",
      });
    }
  }

  async function loadFlags(parentCompanyId: string) {
    if (!parentCompanyId) return;
    setLoadingFlags(true);
    try {
      const res = await request(`/admin/feature-flags/${parentCompanyId}`, {
        method: "get",
      });
      setFlagsData(res.data as FeatureFlagsAdminResponse);
    } catch (err: any) {
      console.error(err);
      toast(err?.response?.data?.message || "Failed to load feature flags", {
        type: "error",
        toastId: "feature-flags-load-failed",
      });
    } finally {
      setLoadingFlags(false);
    }
  }

  useEffect(() => {
    if (!canAccess) return;
    loadTierDefaults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess]);

  async function handleSave() {
    if (!selectedParentCompanyId) return;
    try {
      const res = await request(
        `/admin/feature-flags/${selectedParentCompanyId}`,
        {
          method: "patch",
          data: {
            planTier: draftPlanTier,
            releaseChannel: draftReleaseChannel,
            betaAllowlist: draftBetaAllowlist,
            featureFlagOverrides: draftOverrides,
          },
        }
      );
      setFlagsData(res.data as FeatureFlagsAdminResponse);
      toast("Feature flags updated", {
        type: "success",
        toastId: "feature-flags-saved",
      });
    } catch (err: any) {
      console.error(err);
      toast(err?.response?.data?.message || "Failed to save feature flags", {
        type: "error",
        toastId: "feature-flags-save-failed",
      });
    }
  }

  if (!canAccess) {
    return (
      <div className="p-4">
        <Card>
          <div className="text-gray-800 font-semibold">Not authorized</div>
          <div className="text-gray-600 text-sm">
            You must be a super admin to manage feature flags.
          </div>
        </Card>
      </div>
    );
  }

  const registryEntries = Object.entries(flagsData?.registry || {});

  /**
   * Tenant Flags: group registry keys so module parents (ex: `jobs.enabled`) can expand
   * to show their sub-features (ex: `jobs.tasks.enabled`, `jobs.accessories.enabled`, etc.).
   */
  const tenantRegistryRows = useMemo(() => {
    const entries = registryEntries || [];
    const registry = Object.fromEntries(entries) as Record<string, FeatureMeta>;

    type Group = {
      kind: "group";
      parentKey: string;
      parentMeta: FeatureMeta;
      children: Array<[string, FeatureMeta]>;
    };

    type Standalone = {
      kind: "standalone";
      featureKey: string;
      meta: FeatureMeta;
    };

    const groupsByModule = new Map<string, Group>();
    const standalone: Standalone[] = [];

    // Pass 1: create groups for module parents present in registry
    for (const [featureKey, meta] of entries) {
      if (!isModuleParentKey(featureKey)) continue;
      const module = getModuleFromFeatureKey(featureKey);
      if (!module) continue;
      groupsByModule.set(module, {
        kind: "group",
        parentKey: featureKey,
        parentMeta: meta,
        children: [],
      });
    }

    // Pass 2: assign children to groups when the parent exists; otherwise keep standalone
    for (const [featureKey, meta] of entries) {
      const module = getModuleFromFeatureKey(featureKey);
      const parentKey = getModuleParentKey(featureKey);
      const parentExists = Boolean(registry?.[parentKey]);

      // Parent rows are rendered via groups, don't duplicate as standalone
      if (isModuleParentKey(featureKey) && parentExists) continue;

      if (
        module &&
        parentExists &&
        featureKey !== parentKey &&
        groupsByModule.has(module)
      ) {
        const group = groupsByModule.get(module);
        group?.children.push([featureKey, meta]);
        continue;
      }

      standalone.push({ kind: "standalone", featureKey, meta });
    }

    const groups = Array.from(groupsByModule.values())
      .map((g) => ({
        ...g,
        children: (g.children || []).sort((a, b) => a[0].localeCompare(b[0])),
      }))
      .sort((a, b) => a.parentKey.localeCompare(b.parentKey));

    const standaloneSorted = (standalone || []).sort((a, b) =>
      a.featureKey.localeCompare(b.featureKey)
    );

    return [...groups, ...standaloneSorted] as Array<Group | Standalone>;
  }, [registryEntries]);

  const tabs = [
    {
      id: "tenantFlags",
      title: "Tenant Flags",
      content: (
        <div className="space-y-4">
          <Card>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Select
                label="Tenant (parent company)"
                loading={customersLoading}
                options={toOptions(customerOptions)}
                search
                value={
                  customerOptions.find(
                    (o) => o.value === selectedParentCompanyId
                  )?.label || ""
                }
                onChange={(e: any) => {
                  const id = String(e.value || "");
                  setSelectedParentCompanyId(id);
                  loadFlags(id);
                }}
              />

              <Select
                label="Plan tier"
                options={[
                  { label: "tier1", value: "tier1" },
                  { label: "tier2", value: "tier2" },
                  { label: "tier3", value: "tier3" },
                  { label: "tier4 (superAdmin)", value: "tier4" },
                ]}
                value={draftPlanTier}
                onChange={(e: any) => setDraftPlanTier(e.value)}
              />

              <Select
                label="Release channel"
                options={[
                  { label: "stable", value: "stable" },
                  { label: "latest", value: "latest" },
                ]}
                value={draftReleaseChannel}
                onChange={(e: any) => setDraftReleaseChannel(e.value)}
              />
            </div>

            <div className="mt-4">
              <MultiSelect
                id="betaAllowlist"
                label="Beta allowlist (only used when channel=latest)"
                options={betaFeatureOptions}
                selectedValues={draftBetaAllowlist}
                onChange={(selected: any[]) => {
                  const values = (selected || [])
                    .map((s) => String(s.value))
                    .filter(Boolean);
                  setDraftBetaAllowlist(values);
                }}
              >
                {null}
              </MultiSelect>
            </div>

            <div className="mt-4 flex items-center space-x-2">
              <Button
                disabled={!selectedParentCompanyId || loadingFlags}
                onClick={handleSave}
              >
                Save
              </Button>
              {loadingFlags && <Spinner size="sm" />}
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div className="text-base font-semibold text-gray-900">
                Registry
              </div>
              <div className="text-sm text-gray-600">
                Overrides persist per tenant; sources show where the effective
                value came from.
              </div>
            </div>

            {!flagsData ? (
              <div className="mt-4 text-gray-600 text-sm">
                Select a tenant to load and manage feature flags.
              </div>
            ) : (
              <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-2">
                {tenantRegistryRows.map((row: any) => {
                  const renderRegistryRow = (
                    featureKey: string,
                    meta: FeatureMeta,
                    opts?: { compact?: boolean }
                  ) => {
                    const lifecycle = meta?.lifecycle || "ga";
                    const compact = Boolean(opts?.compact);

                    const hasOverride = Object.prototype.hasOwnProperty.call(
                      draftOverrides,
                      featureKey
                    );
                    const overrideValue = hasOverride
                      ? Boolean(draftOverrides[featureKey])
                      : undefined;
                    const effective = Boolean(
                      flagsData.adminFlags?.[featureKey]
                    );
                    const visibleToTenant =
                      Object.prototype.hasOwnProperty.call(
                        flagsData.resolvedFlags || {},
                        featureKey
                      );
                    const source = flagsData.sources?.[featureKey] || "";

                    const overrideModeValue = !hasOverride
                      ? "inherit"
                      : overrideValue
                      ? "on"
                      : "off";

                    return (
                      <div
                        key={`tenant-registry-${featureKey}`}
                        className={`border border-gray-200 rounded-lg ${
                          compact ? "p-2" : "p-3"
                        } flex flex-col md:flex-row md:items-center md:justify-between gap-3`}
                      >
                        <div className="min-w-0">
                          <div className="flex items-center space-x-2">
                            <div className="font-mono text-sm text-gray-900 truncate">
                              {featureKey}
                            </div>
                            <Badge label={lifecycle} size="sm" />
                            <Badge
                              color={effective ? "green" : "red"}
                              label={effective ? "enabled" : "disabled"}
                              size="sm"
                            />
                            {!visibleToTenant && (
                              <span className="text-xs text-gray-500">
                                (not tenant-visible)
                              </span>
                            )}
                          </div>
                          {meta?.description && !compact && (
                            <div className="text-sm text-gray-600 mt-1">
                              {meta.description}
                            </div>
                          )}
                          <div className="text-xs text-gray-500 mt-1">
                            Effective:{" "}
                            <span className="font-medium">
                              {String(effective)}
                            </span>{" "}
                            {source ? (
                              <>
                                • Source:{" "}
                                <span className="font-medium">{source}</span>
                              </>
                            ) : null}
                          </div>
                        </div>

                        <div className="w-full md:w-56">
                          <Select
                            label="Override"
                            options={[
                              { label: "Inherit", value: "inherit" },
                              { label: "Force On", value: "on" },
                              { label: "Force Off", value: "off" },
                            ]}
                            value={
                              overrideModeValue === "inherit"
                                ? "Inherit"
                                : overrideModeValue === "on"
                                ? "Force On"
                                : "Force Off"
                            }
                            onChange={(e: any) => {
                              const mode = String(e.value);
                              setDraftOverrides((prev) => {
                                const next = { ...(prev || {}) };
                                if (mode === "inherit") {
                                  delete next[featureKey];
                                  return next;
                                }
                                next[featureKey] = mode === "on";
                                return next;
                              });
                            }}
                          />
                        </div>
                      </div>
                    );
                  };

                  if (row.kind === "group") {
                    const children = Array.isArray(row.children)
                      ? row.children
                      : [];
                    const enabledChildrenCount = children.filter(([k]) =>
                      Boolean(flagsData.adminFlags?.[k])
                    ).length;
                    const allChildrenEnabled =
                      enabledChildrenCount === children.length &&
                      children.length > 0;

                    return (
                      <div
                        key={`tenant-registry-group-${row.parentKey}`}
                        className="space-y-2"
                      >
                        {renderRegistryRow(row.parentKey, row.parentMeta)}

                        {children.length > 0 && (
                          <details className="ml-3 border-l border-gray-200 pl-3">
                            <summary className="cursor-pointer select-none flex items-center justify-between text-sm text-gray-700 hover:text-gray-900">
                              <span>Sub features</span>
                              <Badge
                                color={allChildrenEnabled ? "green" : "red"}
                                label={`${enabledChildrenCount}/${children.length}`}
                                size="sm"
                              />
                            </summary>
                            <div className="mt-2 space-y-2">
                              {children.map(([childKey, childMeta]: any) =>
                                renderRegistryRow(childKey, childMeta, {
                                  compact: true,
                                })
                              )}
                            </div>
                          </details>
                        )}
                      </div>
                    );
                  }

                  return renderRegistryRow(row.featureKey, row.meta);
                })}
              </div>
            )}
          </Card>
        </div>
      ),
    },
    {
      id: "tierDefaults",
      title: "Tier Defaults",
      content: (
        <div className="space-y-4">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-base font-semibold text-gray-900">
                  Tier Defaults
                </div>
                <div className="text-sm text-gray-600">
                  Edit which flags are enabled by default for each tier
                  (global). Dev flags are not tier-assignable.
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button onClick={saveTierDefaults} disabled={!tierData}>
                  Save Tier
                </Button>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <Select
                label="Tier"
                options={[
                  { label: "tier1", value: "tier1" },
                  { label: "tier2", value: "tier2" },
                  { label: "tier3", value: "tier3" },
                  { label: "tier4 (superAdmin)", value: "tier4" },
                ]}
                value={selectedTier}
                onChange={(e: any) => setSelectedTier(e.value)}
              />
              <div className="md:col-span-2 text-sm text-gray-500 flex items-center">
                Changes apply to all tenants on this tier (resolver cache is
                cleared on save).
              </div>
            </div>

            {!tierData ? (
              <div className="mt-4 text-sm text-gray-600">
                Loading tier defaults…
              </div>
            ) : (
              <div className="mt-4 space-y-2">
                {tierDefaultsRows.map((row: any) => {
                  const renderToggleRow = (
                    featureKey: string,
                    meta: FeatureMeta,
                    opts?: { compact?: boolean; disabled?: boolean }
                  ) => {
                    const checked = Boolean(draftTierToggles?.[featureKey]);
                    const compact = Boolean(opts?.compact);
                    const disabled = Boolean(opts?.disabled);

                    return (
                      <div
                        key={`tier-${selectedTier}-${featureKey}`}
                        className={`
                          border border-gray-200 rounded-lg
                          ${compact ? "p-2" : "p-3"}
                          flex items-center justify-between gap-3
                          ${disabled ? "opacity-60" : ""}
                        `}
                      >
                        <div className="min-w-0">
                          <div className="flex items-center space-x-2">
                            <div className="font-mono text-sm text-gray-900 truncate">
                              {featureKey}
                            </div>
                            <Badge label={meta?.lifecycle || "ga"} size="sm" />
                            <Badge
                              color={checked ? "green" : "red"}
                              label={checked ? "enabled" : "disabled"}
                              size="sm"
                            />
                          </div>
                          {meta?.description && !compact && (
                            <div className="text-sm text-gray-600 mt-1">
                              {meta.description}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={disabled}
                            onChange={(e) => {
                              const next = e.target.checked;
                              setDraftTierToggles((prev) => ({
                                ...(prev || {}),
                                [featureKey]: next,
                              }));
                            }}
                            className="h-4 w-4"
                          />
                          <span className="text-sm text-gray-700">Enabled</span>
                        </div>
                      </div>
                    );
                  };

                  // Grouped module parent + dropdown of sub-features
                  if (row.kind === "group") {
                    const parentChecked = Boolean(
                      draftTierToggles?.[row.parentKey]
                    );
                    const children = Array.isArray(row.children)
                      ? row.children
                      : [];

                    const enabledChildrenCount = children.filter(([k]) =>
                      Boolean(draftTierToggles?.[k])
                    ).length;
                    const allChildrenEnabled =
                      enabledChildrenCount === children.length &&
                      children.length > 0;

                    return (
                      <div
                        key={`tier-${selectedTier}-${row.parentKey}-group`}
                        className="space-y-2"
                      >
                        {renderToggleRow(row.parentKey, row.parentMeta)}

                        {/* Only show the dropdown when the parent feature is enabled */}
                        {parentChecked && children.length > 0 && (
                          <details className="ml-3 border-l border-gray-200 pl-3">
                            <summary className="cursor-pointer select-none flex items-center justify-between text-sm text-gray-700 hover:text-gray-900">
                              <span className="inline-flex items-center gap-2">
                                <span>Sub features</span>
                                <Badge
                                  color={allChildrenEnabled ? "green" : "red"}
                                  label={`${enabledChildrenCount}/${children.length}`}
                                  size="sm"
                                />
                              </span>
                              <button
                                type="button"
                                className="text-xs px-2 py-1 rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                                disabled={
                                  enabledChildrenCount === children.length
                                }
                                onClick={(e) => {
                                  // Don't toggle the <details> open/closed when clicking the action.
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setDraftTierToggles((prev) => {
                                    const next = { ...(prev || {}) };
                                    children.forEach(([k]) => {
                                      next[k] = true;
                                    });
                                    return next;
                                  });
                                }}
                                title={
                                  enabledChildrenCount === children.length
                                    ? "All sub-features are already enabled"
                                    : "Enable all sub-features"
                                }
                              >
                                Enable all
                              </button>
                            </summary>
                            <div className="mt-2 space-y-2">
                              {children.map(([childKey, childMeta]: any) =>
                                renderToggleRow(childKey, childMeta, {
                                  compact: true,
                                })
                              )}
                            </div>
                          </details>
                        )}
                      </div>
                    );
                  }

                  // Standalone flag
                  return renderToggleRow(row.featureKey, row.meta);
                })}
              </div>
            )}
          </Card>
        </div>
      ),
    },
  ];

  return (
    <div className="p-4 space-y-4">
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-lg font-semibold text-gray-900">
              Feature Flags
            </div>
            <div className="text-sm text-gray-600">
              Manage tier defaults + tenant overrides + beta access (latest
              channel).
            </div>
          </div>
          <div className="text-sm text-gray-500">
            Signed in as: <span className="font-medium">{user?.email}</span>
          </div>
        </div>
      </Card>

      <TabsAny
        id="featureFlagsTab"
        useSearchParams
        tabListClasses="bg-white px-2 h-[38px] border-b border-gray-200 rounded-t-lg"
        tabclassName="mx-1"
        panelClasses="p-0"
        data={tabs}
      />
    </div>
  );
};

export default FeatureFlagsPage;
